import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { apiService } from '../services/apiService';
import toast from 'react-hot-toast';

// Types
export interface CompanyInfo {
  name: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  logo: string;
  taxNumber: string;
  registrationNumber: string;
}

export interface InvoiceTemplate {
  header: {
    showLogo: boolean;
    showCompanyInfo: boolean;
    customHeader: string;
  };
  footer: {
    showTerms: boolean;
    showSignature: boolean;
    customFooter: string;
  };
  layout: {
    columns: string[];
    showProductImage: boolean;
    showBarcode: boolean;
  };
  styling: {
    fontSize: string;
    lineHeight: string;
    pageSize: string;
  };
}

export interface InvoiceSettings {
  numbering: {
    prefix: string;
    suffix: string;
    startNumber: number;
    format: string;
  };
  defaultCurrency: string;
  defaultCurrencySymbol: string;
  taxRate: number;
  template: InvoiceTemplate;
}

export interface DashboardWidget {
  id: string;
  type: 'chart' | 'table' | 'metric' | 'list';
  title: string;
  position: {
    x: number;
    y: number;
    w: number;
    h: number;
  };
  config: Record<string, any>;
  isVisible: boolean;
}

export interface DashboardSettings {
  widgets: DashboardWidget[];
  layout: 'grid' | 'list' | 'custom';
}

export interface SystemSettings {
  timezone: string;
  dateFormat: string;
  timeFormat: string;
  language: string;
  enableNotifications: boolean;
  enableAuditLog: boolean;
  maxFileSize: number;
  allowedFileTypes: string[];
}

export interface BarcodeSettings {
  format: string;
  width: number;
  height: number;
  includeText: boolean;
}

export interface EmailSettings {
  smtp: {
    host: string;
    port: number;
    secure: boolean;
    auth: {
      user: string;
      pass: string;
    };
  };
  from: string;
  templates: {
    invoice: {
      subject: string;
      body: string;
    };
  };
}

export interface CustomField {
  name: string;
  type: 'text' | 'number' | 'date' | 'select' | 'textarea';
  required: boolean;
  options: string[];
}

export interface CustomFields {
  products: CustomField[];
  invoices: CustomField[];
}

export interface Settings {
  company: CompanyInfo;
  invoice: InvoiceSettings; // legacy/general
  purchaseInvoice: InvoiceSettings;
  deliveryInvoice: InvoiceSettings;
  returnInvoice: InvoiceSettings;
  dashboard: DashboardSettings;
  system: SystemSettings;
  barcode: BarcodeSettings;
  email: EmailSettings;
  customFields: CustomFields;
  updatedBy?: string;
  updatedAt?: Date;
}

interface SettingsState {
  settings: Settings | null;
  loading: boolean;
  error: string | null;
}

// Action types
type SettingsAction =
  | { type: 'SETTINGS_LOADING' }
  | { type: 'SETTINGS_SUCCESS'; payload: Settings }
  | { type: 'SETTINGS_ERROR'; payload: string }
  | { type: 'UPDATE_SETTINGS'; payload: Partial<Settings> }
  | { type: 'ADD_WIDGET'; payload: DashboardWidget }
  | { type: 'UPDATE_WIDGET'; payload: { id: string; updates: Partial<DashboardWidget> } }
  | { type: 'REMOVE_WIDGET'; payload: string }
  | { type: 'ADD_CUSTOM_FIELD'; payload: { type: 'products' | 'invoices'; field: CustomField } }
  | { type: 'UPDATE_CUSTOM_FIELD'; payload: { type: 'products' | 'invoices'; name: string; updates: Partial<CustomField> } }
  | { type: 'REMOVE_CUSTOM_FIELD'; payload: { type: 'products' | 'invoices'; name: string } };

// Initial state
const initialState: SettingsState = {
  settings: null,
  loading: true,
  error: null,
};

// Reducer
const settingsReducer = (state: SettingsState, action: SettingsAction): SettingsState => {
  switch (action.type) {
    case 'SETTINGS_LOADING':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'SETTINGS_SUCCESS':
      return {
        ...state,
        settings: action.payload,
        loading: false,
        error: null,
      };
    case 'SETTINGS_ERROR':
      return {
        ...state,
        settings: null,
        loading: false,
        error: action.payload,
      };
    case 'UPDATE_SETTINGS':
      return {
        ...state,
        settings: state.settings ? { ...state.settings, ...action.payload } : null,
      };
    case 'ADD_WIDGET':
      return {
        ...state,
        settings: state.settings ? {
          ...state.settings,
          dashboard: {
            ...state.settings.dashboard,
            widgets: [...state.settings.dashboard.widgets, action.payload],
          },
        } : null,
      };
    case 'UPDATE_WIDGET':
      return {
        ...state,
        settings: state.settings ? {
          ...state.settings,
          dashboard: {
            ...state.settings.dashboard,
            widgets: state.settings.dashboard.widgets.map(widget =>
              widget.id === action.payload.id
                ? { ...widget, ...action.payload.updates }
                : widget
            ),
          },
        } : null,
      };
    case 'REMOVE_WIDGET':
      return {
        ...state,
        settings: state.settings ? {
          ...state.settings,
          dashboard: {
            ...state.settings.dashboard,
            widgets: state.settings.dashboard.widgets.filter(widget => widget.id !== action.payload),
          },
        } : null,
      };
    case 'ADD_CUSTOM_FIELD':
      return {
        ...state,
        settings: state.settings ? {
          ...state.settings,
          customFields: {
            ...state.settings.customFields,
            [action.payload.type]: [...state.settings.customFields[action.payload.type], action.payload.field],
          },
        } : null,
      };
    case 'UPDATE_CUSTOM_FIELD':
      return {
        ...state,
        settings: state.settings ? {
          ...state.settings,
          customFields: {
            ...state.settings.customFields,
            [action.payload.type]: state.settings.customFields[action.payload.type].map(field =>
              field.name === action.payload.name
                ? { ...field, ...action.payload.updates }
                : field
            ),
          },
        } : null,
      };
    case 'REMOVE_CUSTOM_FIELD':
      return {
        ...state,
        settings: state.settings ? {
          ...state.settings,
          customFields: {
            ...state.settings.customFields,
            [action.payload.type]: state.settings.customFields[action.payload.type].filter(
              field => field.name !== action.payload.name
            ),
          },
        } : null,
      };
    default:
      return state;
  }
};

// Context
interface SettingsContextType {
  settings: Settings | null;
  loading: boolean;
  error: string | null;
  loadSettings: () => Promise<void>;
  updateSettings: (updates: Partial<Settings>) => Promise<void>;
  updateCompany: (company: Partial<CompanyInfo>) => Promise<void>;
  updateInvoiceSettings: (type: 'purchase' | 'delivery' | 'return', settings: Partial<InvoiceSettings>) => Promise<void>;
  updateSystemSettings: (settings: Partial<SystemSettings>) => Promise<void>;
  updateBarcodeSettings: (settings: Partial<BarcodeSettings>) => Promise<void>;
  updateEmailSettings: (settings: Partial<EmailSettings>) => Promise<void>;
  addWidget: (widget: DashboardWidget) => Promise<void>;
  updateWidget: (id: string, updates: Partial<DashboardWidget>) => Promise<void>;
  removeWidget: (id: string) => Promise<void>;
  addCustomField: (type: 'products' | 'invoices', field: CustomField) => Promise<void>;
  updateCustomField: (type: 'products' | 'invoices', name: string, updates: Partial<CustomField>) => Promise<void>;
  removeCustomField: (type: 'products' | 'invoices', name: string) => Promise<void>;
  resetSettings: () => Promise<void>;
  getSetting: (path: string) => any;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

// Provider component
interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(settingsReducer, initialState);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  // Load settings function
  const loadSettings = async () => {
    try {
      console.log('🔄 Frontend: loadSettings called');
      dispatch({ type: 'SETTINGS_LOADING' });
      const response = await apiService.get('/settings', { 
        forceRefresh: true,
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      console.log('📥 Frontend: Settings received from API:', JSON.stringify(response.data.company, null, 2));
      dispatch({ type: 'SETTINGS_SUCCESS', payload: response.data });
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to load settings';
      console.error('❌ Frontend: Error loading settings:', error);
      dispatch({ type: 'SETTINGS_ERROR', payload: errorMessage });
      toast.error(errorMessage);
    }
  };

  // Update settings function
  const updateSettings = async (updates: Partial<Settings>) => {
    try {
      const response = await apiService.put('/settings', updates);
      dispatch({ type: 'UPDATE_SETTINGS', payload: response.data.settings });
      toast.success('Settings updated successfully');
      await loadSettings(); // Ensure settings are always fresh
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to update settings';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Update company function
  const updateCompany = async (company: Partial<CompanyInfo>) => {
    try {
      console.log('🔄 Frontend: updateCompany called with data:', JSON.stringify(company, null, 2));
      const response = await apiService.put('/settings/company', company);
      console.log('📥 Frontend: Update response received:', JSON.stringify(response.data.company, null, 2));
      dispatch({ type: 'UPDATE_SETTINGS', payload: { company: response.data.company } });
      toast.success('Company information updated successfully');
      console.log('🔄 Frontend: Calling loadSettings after update');
      await loadSettings(); // Ensure settings are always fresh after update
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to update company information';
      console.error('❌ Frontend: Error updating company:', error);
      toast.error(errorMessage);
      throw error;
    }
  };

  // Update invoice settings function
  const updateInvoiceSettings = async (type: 'purchase' | 'delivery' | 'return', settings: Partial<InvoiceSettings>) => {
    try {
      const response = await apiService.put(`/settings/invoice/${type}`, settings);
      dispatch({ type: 'UPDATE_SETTINGS', payload: { [`${type}Invoice`]: response.data.invoice } });
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} Invoice settings updated successfully`);
      await loadSettings(); // Ensure settings are always fresh after update
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || `Failed to update ${type} invoice settings`;
      toast.error(errorMessage);
      throw error;
    }
  };

  // Update system settings function
  const updateSystemSettings = async (settings: Partial<SystemSettings>) => {
    try {
      const response = await apiService.put('/settings/system', settings);
      dispatch({ type: 'UPDATE_SETTINGS', payload: { system: response.data.system } });
      toast.success('System settings updated successfully');
      await loadSettings(); // Ensure settings are always fresh after update
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to update system settings';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Update barcode settings function
  const updateBarcodeSettings = async (settings: Partial<BarcodeSettings>) => {
    try {
      const response = await apiService.put('/settings/barcode', settings);
      dispatch({ type: 'UPDATE_SETTINGS', payload: { barcode: response.data.barcode } });
      toast.success('Barcode settings updated successfully');
      await loadSettings(); // Ensure settings are always fresh after update
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to update barcode settings';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Update email settings function
  const updateEmailSettings = async (settings: Partial<EmailSettings>) => {
    try {
      const response = await apiService.put('/settings/email', settings);
      dispatch({ type: 'UPDATE_SETTINGS', payload: { email: response.data.email } });
      toast.success('Email settings updated successfully');
      await loadSettings(); // Ensure settings are always fresh after update
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to update email settings';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Add widget function
  const addWidget = async (widget: DashboardWidget) => {
    try {
      const response = await apiService.post('/settings/dashboard/widgets', widget);
      dispatch({ type: 'ADD_WIDGET', payload: response.data.widget });
      toast.success('Widget added successfully');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to add widget';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Update widget function
  const updateWidget = async (id: string, updates: Partial<DashboardWidget>) => {
    try {
      const response = await apiService.put(`/api/settings/dashboard/widgets/${id}`, updates);
      dispatch({ type: 'UPDATE_WIDGET', payload: { id, updates: response.data.widget } });
      toast.success('Widget updated successfully');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to update widget';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Remove widget function
  const removeWidget = async (id: string) => {
    try {
      await apiService.delete(`/api/settings/dashboard/widgets/${id}`);
      dispatch({ type: 'REMOVE_WIDGET', payload: id });
      toast.success('Widget removed successfully');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to remove widget';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Add custom field function
  const addCustomField = async (type: 'products' | 'invoices', field: CustomField) => {
    try {
      const response = await apiService.post(`/api/settings/custom-fields/${type}`, field);
      dispatch({ type: 'ADD_CUSTOM_FIELD', payload: { type, field: response.data.field } });
      toast.success('Custom field added successfully');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to add custom field';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Update custom field function
  const updateCustomField = async (type: 'products' | 'invoices', name: string, updates: Partial<CustomField>) => {
    try {
      const response = await apiService.put(`/api/settings/custom-fields/${type}/${name}`, updates);
      dispatch({ type: 'UPDATE_CUSTOM_FIELD', payload: { type, name, updates: response.data.field } });
      toast.success('Custom field updated successfully');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to update custom field';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Remove custom field function
  const removeCustomField = async (type: 'products' | 'invoices', name: string) => {
    try {
      await apiService.delete(`/api/settings/custom-fields/${type}/${name}`);
      dispatch({ type: 'REMOVE_CUSTOM_FIELD', payload: { type, name } });
      toast.success('Custom field removed successfully');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to remove custom field';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Reset settings function
  const resetSettings = async () => {
    try {
      const response = await apiService.post('/settings/reset');
      dispatch({ type: 'SETTINGS_SUCCESS', payload: response.data.settings });
      toast.success('Settings reset to defaults successfully');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to reset settings';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Get setting by path function
  const getSetting = (path: string): any => {
    if (!state.settings) return null;
    return path.split('.').reduce((obj, key) => obj && (obj as any)[key], state.settings);
  };

  const value: SettingsContextType = {
    settings: state.settings,
    loading: state.loading,
    error: state.error,
    loadSettings,
    updateSettings,
    updateCompany,
    updateInvoiceSettings,
    updateSystemSettings,
    updateBarcodeSettings,
    updateEmailSettings,
    addWidget,
    updateWidget,
    removeWidget,
    addCustomField,
    updateCustomField,
    removeCustomField,
    resetSettings,
    getSetting,
  };

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

// Hook to use settings context
export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}; 